<template>
  <div class="outcomes-report">
    <div class="col-12 text-right">
      <form-component>
        <div class="form-row">
          <input-text
            id="search"
            v-model="search"
            :value="search"
            name="search"
            label="search"
            @input="search=$event;delay('filter-outcomes', resetToPageZero, 500);"
          />
          <outcomes-filter
            :value="outcomesFilter"
            @input="outcomesFilter=$event;delay('filter-outcomes', resetToPageZero, 500);"
          />
          <input-datepicker
            id="start_time_after"
            :value="from"
            name="start_time_after"
            label="From"
            @input="setFrom($event);resetToPageZero();"
          />
          <input-datepicker
            id="start_time_before"
            :value="through"
            name="start_time_before"
            label="Through"
            @input="setThrough($event);resetToPageZero();"
          />
          <b-tooltip
            v-if="!from || !through || fetching"
            target="outcomesReportExportButtonDiv"
          >
            Must select from and through dates to export.
          </b-tooltip>
          <div id="outcomesReportExportButtonDiv" class="btn-export">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!from || !through || fetching"
              @click="exportReport"
            >
              Export
            </button>
          </div>
        </div>
      </form-component>
    </div>
    <b-table
      no-local-sorting
      striped
      :fields="sessionFields"
      :items="outcomes"
      head-variant="dark"
      @sort-changed="sortChanged">
      <template #cell(counselor)="row">
        <a :href="`/counselor/profile/${row.item.counselor.id}`">
          {{ row.item.counselor.firstName }} {{ row.item.counselor.lastName }}
        </a>
      </template>
      <template #cell(client)="row">
        <a :href="`/student/${row.item.client.userId}`">
          {{ row.item.client.firstName }} {{ row.item.client.lastName }}
        </a>
      </template>
      <template #cell(campus)="row">
        {{ row.item.snapshotCampus.name }}
      </template>
      <template #cell(start)="row">
        {{ row.item.start | withTimeZone | format('M/D/YYYY h:mmA (z)') }}
      </template>
      <template #cell(type-color)="row">
        <div class="outcome-type">
          <div v-if="row.item.type === 'Successful'" class="good" />
          <div v-else-if="row.item.type === 'CounselorCanceled'" class="normal" />
          <div v-else-if="row.item.type === 'UserCanceled'" class="normal" />
          <div v-else-if="row.item.type === 'UserLateCanceled'" class="warn" />
          <div v-else-if="row.item.type === 'AdminCanceled'" class="normal" />
          <div v-else-if="row.item.type === 'CounselorNoShow'" class="bad" />
          <div v-else-if="row.item.type === 'UserNoShow'" class="warn" />
          <div v-else-if="row.item.type === 'BothNoShow'" class="bad" />
          <div v-else class="warn" />
        </div>
      </template>
      <template #cell(type)="row">
        <div class="outcome-type">
          <template v-if="row.item.type === 'Unknown'">
            <span>Unknown</span>
          </template>
          <template v-else-if="row.item.type === 'Successful'">
            <span>Successful</span>
          </template>
          <template v-else-if="row.item.type === 'CounselorCanceled'">
            <span>Canceled - Counselor</span>
          </template>
          <template v-else-if="row.item.type === 'UserCanceled'">
            <span>Canceled - Client</span>
          </template>
          <template v-else-if="row.item.type === 'UserLateCanceled'">
            <span>Canceled - Client (Late)</span>
          </template>
          <template v-else-if="row.item.type === 'AdminCanceled'">
            <span>Canceled - Admin</span>
          </template>
          <template v-else-if="row.item.type === 'CounselorNoShow'">
            <span>No Show - Counselor</span>
          </template>
          <template v-else-if="row.item.type === 'UserNoShow'">
            <span>No Show - Client</span>
          </template>
          <template v-else-if="row.item.type === 'BothNoShow'">
            <span>No Show - Both</span>
          </template>
        </div>
      </template>
      <template #cell(comment)="row">
        <template v-if="row.item.publicExplainer || row.item.internalExplainer ">
          <font-awesome-icon
            v-b-tooltip.hover.top="'Outcome has a note.'"
            :icon="['fas', 'comment-lines']"
            class="icon alt"
          />
        </template>
      </template>
      <template #cell(payment)="row">
        {{ row.item.payment.status }}
        <template v-if="row.item.payment.status === 'Paid'">
          <template v-if="row.item.payment.type === 'CampusCredit'">
            (Credit)
          </template>
        </template>
      </template>
      <template #cell(show_details)="row">
        <div v-if="!row.item._showDetails">
          <button
            v-if="['Canceled', 'Refunded'].some(s => s === row.item.payment.status)"
            class="btn btn-ghost"
            type="button"
            @click="toggleReinvoicePaymentConfirm(row)"
          >
            Re-Invoice
          </button>
          <button
            v-if="row.item.payment.status === 'Paid'"
            class="btn btn-ghost"
            type="button"
            @click="toggleRefundPaymentConfirm(row)"
          >
            Refund
          </button>
          <button
            v-else-if="row.item.payment.status === 'Pending'"
            class="btn btn-ghost"
            type="button"
            @click="toggleForgivePaymentConfirm(row)"
          >
            Forgive
          </button>
          <button class="btn btn-ghost" type="button" @click="toggleMoreDetails(row)">
            More
          </button>
        </div>
        <div v-else-if="row.item.mode === 'details'">
          <button class="btn btn-ghost" type="button" @click="toggleEditMode(row)">
            Edit
          </button>
          <button class="btn btn-ghost" type="button" @click="row.toggleDetails()">
            Close
          </button>
        </div>
      </template>
      <template #row-details="row">
        <Loader v-if="acting" style="width: 100%;" />
        <div v-else-if="row.item.mode === 'edit'">
          <edit-outcome
            :outcome="row.item"
            @invalid="displayErrorMessage"
            @save="updateOutcome(row.item.id, $event)"
            @cancel="row.toggleDetails();toggleMoreDetails(row);"
          />
        </div>
        <div v-else-if="row.item.mode === 'details'">
          <div style="display:flex;">
            <div class="mr-5">
              <table>
                <th>Attendance</th>
                <th></th>
                <th></th>
                <tr v-if="!row.item.attendances || !row.item.attendances.length">
                  No attendance data found.
                </tr>
                <tr v-for="(attendance, index) in row.item.attendances" :key="index">
                  <td>{{ attendance.user.firstName }} {{ attendance.user.lastName }}</td>
                  <td>{{ attendance.event }}</td>
                  <td>{{ attendance.when | withTimeZone | format('M/D/YYYY h:mmA (z)') }}</td>
                </tr>
              </table>
            </div>
            <div>
              <div v-if="row.item.internalExplainer">
                <h3>Internal Notes</h3>
                <p>{{ row.item.internalExplainer }}</p>
              </div>
              <div v-if="row.item.publicExplainer">
                <h3>Public Notes</h3>
                <p>{{ row.item.publicExplainer }}</p>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="row.item.mode === 'confirm'" class="row-details-container">
          <div class="message">
            {{ row.item.message }}
          </div>
          <div class="buttons">
            <button class="btn btn-warn" type="button" @click="row.item.action(row.item)">
              Confirm
            </button>
            <button class="btn btn-ghost" type="button" @click="row.toggleDetails()">
              Back
            </button>
          </div>
        </div>
      </template>
    </b-table>
    <Loader v-if="fetching" style="width: 100%;" />
    <div v-else-if="!outcomes.length" class="text-center">
      No session outcomes found.
    </div>
    <div v-else-if="!pageToken" class="text-center">
      All records loaded.
    </div>
    <button
      v-else
      type="button"
      class="btn btn-primary"
      @click="fetchNextRecords()"
    >
      Load More
    </button>
  </div>
</template>

<script>
import Loader from '@/components/Loader.vue';
import BillingsClient from '@/api/BillingsClient';
import SessionClient from '@/api/SessionClient';
import MeetingsClient from '@/api/MeetingsClient';
import InputText from '@/components/inputs/InputText.vue';
import InputDatepicker from '@/components/inputs/InputDatepicker.vue';
import FormComponent from '@/components/FormComponent.vue';
import downloadCsv from '@/helpers/mixins/download-csv.js';
import delay from '@/helpers/mixins/delay.js';
import urlQueryEncoder from '@/helpers/mixins/url-query-encoder.js';
import moment from 'moment';
import OutcomesFilter from './OutcomesFilter.vue';
import EditOutcome from './EditOutcome.vue';

export default {
    name: 'OutcomesReport',
    components:{
        Loader,
        InputDatepicker,
        InputText,
        FormComponent,
        OutcomesFilter,
        EditOutcome,
    },
    mixins:[downloadCsv, delay, urlQueryEncoder],
    data() {
        return {
            billingsClient: new BillingsClient(this.$http),
            sessionsClient: new SessionClient(this.$http),
            meetingsClient: new MeetingsClient(this.$http),
            sessionFields: [
                { key: 'counselor', label: 'Counselor', sortable: true, sortAsc: 'ListOutcomesByCounselorNameAscending', sortDesc: 'ListOutcomesByCounselorNameDescending' },
                { key: 'client', label: 'Client', sortable: true, sortAsc: 'ListOutcomesByClientNameAscending', sortDesc: 'ListOutcomesByClientNameDescending' },
                { key: 'campus', label: 'Campus', sortable: true, sortAsc: 'ListOutcomesByCampusNameAscending', sortDesc: 'ListOutcomesByCampusNameDescending' },
                { key: 'start', label: 'Start', sortable: true, sortAsc: 'ListOutcomesByStartAscending', sortDesc: 'ListOutcomesByStartDescending' },
                { key: 'type-color', label: '' },
                { key: 'type', label: 'Outcome', sortable: true, sortAsc: 'ListOutcomesByOutcomeTypeAscending', sortDesc: 'ListOutcomesByOutcomeTypeDescending' },
                { key: 'comment', label: '' },
                { key: 'payment', label: 'Payment', sortable: true, sortAsc: 'ListOutcomesByPaymentStatusAscending', sortDesc: 'ListOutcomesByPaymentStatusDescending' },
                { key: 'show_details', label: '' }
            ],

            search: null,
            outcomesFilter: [],
            from: null,
            through: null,
            page: 0,
            customSort: null,

            acting: false,
            fetching: false,
            pageToken: 'ListOutcomesByStartDescending',
            outcomes: [],
        }
    },
    destroyed() {
        removeEventListener('scroll', (event) => this.trackScroll(event));
    },
    async mounted() {
      this.loadSortsAndFiltersFromQuery();

      await this.resetToCurrentPage();

      window.scrollTo(0, this.scroll);
      addEventListener('scroll', (event) => this.trackScroll(event));
    },
    methods: {
      loadSortsAndFiltersFromQuery() {
        const SuperAdminAppointments = this.getUrlQuery('SuperAdminAppointments');
        if (!SuperAdminAppointments) 
          return;

        const { 
          from,
          through,
          search,
          page,
          scroll,
          outcomesFilter,
          customSort
        } = SuperAdminAppointments;
        
        this.setFrom(from);
        this.setThrough(through);
        
        if (search)
          this.search = search;

        if (scroll)
          this.scroll = Number(scroll);

        if (outcomesFilter)
          this.outcomesFilter = outcomesFilter;
        
        if (page)
          this.page = Number(page);
        
        if(customSort && this.sessionFields.find(x => x.sortAsc === customSort || x.sortDesc === customSort))
          this.customSort = customSort;
      },
      updateQuery() {
        this.updateUrlQuery('SuperAdminAppointments', {
          from: this.from || undefined,
          through: this.through || undefined,
          search: this.search || undefined,
          page: this.page || undefined,
          scroll: this.scroll || undefined,
          outcomesFilter: this.outcomesFilter && this.outcomesFilter.length
              ? this.outcomesFilter
              : undefined,
          customSort: this.customSort || undefined,
        });
      },

      resetToPageZero() {
        return this.reset(0, this.customSort);
      },
      resetToCurrentPage() {
        return this.reset(this.page, this.customSort);
      },
      reset(targetPage, pageToken) {
        if (targetPage == null || targetPage == undefined)
          targetPage = 0;
        if (!pageToken)
          pageToken = 'ListOutcomesByStartDescending'

        this.pageToken = pageToken;
        this.page = 0;
        this.outcomes = [];

        return this.fetchNextRecords(targetPage);
      },

      setFrom(from) {
          if (!from || moment(from).isValid())
              this.from = from;
          
          if (this.through && from > this.through)
              this.through = this.from;
      },
      setThrough(through) {
          if (!through || moment(through).isValid())
              this.through = through;

          if (this.from && this.through && this.from > through)
              this.from = this.through;
      },
      trackScroll() {
          this.scroll = window.scrollY;
          this.updateQuery();
      },
      sortChanged(change) {
        const { sortBy, sortDesc } = change;
        const column = this.sessionFields.find(x => x.key === sortBy);
        if (!column) {
          console.error('Could not find sorting column.')
          return;
        }
        this.customSort = sortDesc ? column.sortDesc : column.sortAsc;

        return this.resetToPageZero();
      },

      async fetchNextRecords(targetPage = 0) {
          try {
              this.fetching = true;

              do {
                  const response = await this.fetch(this.pageToken, 20);
                  
                  this.page++;
                  this.pageToken = response.nextPageToken;
                  
                  response.outcomes.forEach(outcome => {
                      this.outcomes.push({
                          ...outcome,
                          start: moment(outcome.start),
                          end: moment(outcome.end),
                          snapshotCampus: outcome.snapshotCampus == null ? {
                            id: 0,
                            name: 'Unassigned',
                          } : outcome.snapshotCampus
                      });
                  });
              } while (this.page < targetPage && this.pageToken)

              this.updateQuery();
          } catch (ex) {
              console.error(ex);
          } finally {
              this.fetching = false;
          }
      },
      async fetch(pageToken, recordCount) {
          let search = undefined;
          if (this.search) {
              search = this.search
                  .split(/\s+/) // match all whitespace
                  .filter(Boolean) // remove empty
          }

          let fromDate = undefined;
          if (this.from)
              fromDate = moment(this.from).startOf('day').toISOString();

          let throughDate = undefined;
          if (this.through) 
              throughDate = moment(this.through).endOf('day').toISOString()

          let outcomesFilter = undefined;
          if (this.outcomesFilter && this.outcomesFilter.length)
              outcomesFilter = this.outcomesFilter;

          return await this.sessionsClient.getOutcomesForAdmin(
              fromDate,
              throughDate,
              search,
              outcomesFilter,
              recordCount,
              pageToken);
      },

      async toggleMoreDetails(row) {
          row.item.mode = 'details';
          if (!row.item.attendance) {
              try {
                  this.acting = true;
                  const response = await this.meetingsClient.getAttendances([row.item.meetingId]);
                  row.item.attendances = response.attendances.map(x => ({
                      ...x,
                      when: moment(x.when),
                  }));
              } catch (ex) {
                  console.error(ex);
              } finally {
                  this.acting = false;
              }
          }
          row.toggleDetails();
      },
      toggleRefundPaymentConfirm(row) {
          row.item.mode = 'confirm';
          row.item.action = this.refundPayment.bind(this);
          row.item.message = `Are you sure you want to refund this session's payment?`;
          row.toggleDetails();
      },
      toggleForgivePaymentConfirm(row) {
          row.item.mode = 'confirm';
          row.item.action = this.refundPayment.bind(this);
          row.item.message = `Are you sure you want to cancel this session's payment?`;
          row.toggleDetails();
      },
      toggleEditMode(row) {
          row.item.mode = 'edit';
          row.toggleDetails();
          row.toggleDetails();
      },
      toggleReinvoicePaymentConfirm(row) {
          row.item.mode = 'confirm';
          row.item.action = this.reinvoicePayment.bind(this);
          row.item.message = `Are you sure you want to re-invoice this session?`;
          row.toggleDetails();
      },

      async updateOutcome(sessionId, changes) {
          try {
              this.acting = true;

              await this.sessionsClient.updateOutcome(
                  sessionId,
                  changes.type,
                  changes.publicExplainer,
                  changes.internalExplainer);

              this.resetToCurrentPage();
          } catch (ex) {
              console.error(ex);
              this.displayErrorMessage(
                  'Failed updating outcome. Please try again. If the issue persists, please reach out to Product.');
          } finally {
              this.acting = false;
          }
      },
      async refundPayment(session) {
          try {
              this.acting = true;
              await this.billingsClient.forgive(session.payment.id)
              this.$store.commit('setGlobalAlert', {
                  msg: 'Payment Forgiven.',
              });
          } catch (ex) {
              console.error(ex);
              this.$store.commit('setGlobalAlert', {
                  msg: 'Failed to forgive session. Please try again!', error: true,
              });
          } finally {
              this.acting = false;
          }
          this.resetToCurrentPage();
      },
      async reinvoicePayment(session) {
        try {
          this.acting = true;
          await this.billingsClient.reinvoice(session.payment.id)
              this.$store.commit('setGlobalAlert', {
                  msg: 'Payment Re-invoiced.',
              });
        } catch (ex) {
          console.error(ex);
              this.$store.commit('setGlobalAlert', {
                  msg: 'Failed to re-invoice session. Please try again!', error: true,
              });
          } finally {
              this.acting = false;
          }
          this.resetToCurrentPage();
      },
      async exportReport() {
          try {
              this.fetching = true;

              let pageToken = 'ListOutcomesByStartDescending';
              const records = [];
              do {
                  const response = await this.fetch(pageToken, 50);

                  pageToken = response.nextPageToken;

                  records.push(...response.outcomes);
              } while (pageToken)

              const csv = this.toCsv(records, [
                  { header: 'Counseler', mapper: (x) => `${x.counselor.firstName} ${x.counselor.lastName}` },
                  { header: 'Client', mapper: (x) => `${x.client.firstName} ${x.client.lastName}` },
                  { header: 'Campus', mapper: (x) => (x.snapshotCampus == null ? 'Unassigned' : x.snapshotCampus.name) },
                  { header: 'SessionStart', mapper: (x) => x.start },
                  { header: 'OutcomeType', mapper: (x) => x.type },
                  { header: 'PaymentStatus', mapper: (x) => x.payment.status },
                  { header: 'PaymentType', mapper: (x) => x.payment.type },
              ])

              this.downloadCsv(csv, `outcomes_${moment().valueOf()}.csv`);
          } catch (ex) {
              console.error(ex);
          } finally {
              this.fetching = false;
          }
      },
      displayErrorMessage(message) {
        this.$store.commit('setGlobalAlert', { msg: message, error: true, });
      },
    }
}
</script>

<style lang="scss">
.outcomes-report {
    .form__input {
        margin-right: 10px;
    }
    div.btn-export {
        align-self: center;
    }
    .b-table {
        color: $white;
        .thead-dark th{
            background-color: $dark-blue !important;
            border-color: $border-color !important;
        }

        .canceled-bar {
            border-radius: 5px;
            width: 5px;
            height: 25px;
            background-color: $red;
        }
        .row-details-container {
            margin-bottom: 25px;

            div.message {
                margin-top: 25px;
                margin-bottom: 25px;
            }
        }
        button.btn {
            margin-right: 5px;
        }
        button.btn-warn {
            color: $white;
            background-color: $red;
        }
        .outcome-type  {
          .bad {
            display: inline-block;
            border-radius: 5px;
            width: 5px;
            height: 25px;
            background-color: $red;
          }
          .warn {
            display: inline-block;
            border-radius: 5px;
            width: 5px;
            height: 25px;
            background-color: $yellow;
          }
          .normal {
            display: inline-block;
            border-radius: 5px;
            width: 5px;
            height: 25px;
            background-color: $light-blue;
          }
          .good {
            display: inline-block;
            border-radius: 5px;
            width: 5px;
            height: 25px;
            background-color: $approval-green;
          }
        }
    }
}
</style>
